import { createContext, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';

export const PermissionsContext = createContext(null);

/**
 * Derives the effective role from the SWA auth profile.
 *
 * The API derives `profile.role` from the Key Vault `admin-identifiers`
 * setting. The SPA uses that server-derived role for privileged page
 * visibility; Entra group claims are intentionally not required.
 */
export function PermissionsProvider({ children }) {
  const { profile } = useAuth();

  const value = useMemo(() => {
    if (!profile) {
      return { role: 'anonymous', adminLevel: 0, isAdmin: false, groups: [], can: () => false };
    }

    const isAdmin = profile.role === 'admin';
    const parsedLevel = Number(profile.adminLevel);
    const adminLevel = isAdmin && Number.isInteger(parsedLevel) && parsedLevel >= 0 && parsedLevel <= 2
      ? parsedLevel
      : 0;
    const role = profile.role || 'authenticated';

    const permissionMap = {
      viewDashboard: true,
      viewVerification: true,
      viewUsers: isAdmin,
      editUsers: isAdmin && adminLevel >= 1,
      deleteUsers: isAdmin && adminLevel >= 2,
      manageUsers: isAdmin && adminLevel >= 1,
      manageSettings: isAdmin,
      viewRestaurants: isAdmin,
      viewRestaurantDetails: isAdmin,
      editRestaurants: isAdmin && adminLevel >= 1,
      deleteRestaurants: isAdmin && adminLevel >= 2,
      viewNotifications: isAdmin,
      updateNotifications: isAdmin && adminLevel >= 1,
      storageHygiene: isAdmin && adminLevel >= 1,
      scopedImageSync: isAdmin && adminLevel >= 1,
      onboarding: isAdmin && adminLevel >= 2,
      imageSyncTool: isAdmin && adminLevel >= 2,
    };

    const can = (action) => Boolean(permissionMap[action]);

    return {
      role,
      isAdmin,
      adminLevel,
      isDataManager: isAdmin && profile.dataManagement === true,
      groups: [],
      can,
    };
  }, [profile]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}
